import React, { useMemo, useRef, useState } from "react";
import ClientSideReactTable from "commons/table/ClientSideReactTable";
import { ColumnTypes } from "commons/table/Constants";
import DismissableModal from "components/DismissableModal";
import { MdErrorOutline, MdRefresh } from "react-icons/md";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import useAggregatedSummaryList from "./AggregatedSummaryList.hook";
import { ROW_STATUS, ROW_STATUS_KEY } from "./AggregatedSummaryList.utils";

const RowShimmer = () => (
  <span
    className="placeholder-glow d-inline-block"
    style={{ width: "80%", minWidth: 48 }}
    aria-hidden="true"
  >
    <span className="placeholder col-12 d-block" style={{ minHeight: 14 }} />
  </span>
);

const RowLoadError = () => (
  <span
    className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-1 small text-danger bg-danger bg-opacity-10 border border-danger border-opacity-25"
    role="status"
    title="Unable to load row data"
  >
    <MdErrorOutline size={15} aria-hidden />
    Unable to load
  </span>
);

const wrapColumnsWithRowLoading = (columns, options = {}) => {
  const {
    staticColumnAccessors = [],
    statusKey = ROW_STATUS_KEY,
  } = options;

  const firstDynamicColumn = columns.find((column) => {
    const accessor = column.accessor || column.accessorKey;
    return !staticColumnAccessors.includes(accessor);
  });
  const firstDynamicAccessor =
    firstDynamicColumn?.accessor || firstDynamicColumn?.accessorKey;

  return columns.map((column) => {
    const accessor = column.accessor || column.accessorKey;
    if (staticColumnAccessors.includes(accessor)) {
      return column;
    }

    const originalCustomComponent = column.customComponent;
    const originalColumnType = column.columnType;
    const isFirstDynamicColumn = accessor === firstDynamicAccessor;

    return {
      ...column,
      columnType: ColumnTypes.Custom,
      customComponent: (value, row) => {
        const status = row.original[statusKey];
        if (status === ROW_STATUS.LOADING) {
          return <RowShimmer />;
        }
        if (status === ROW_STATUS.ERROR) {
          if (isFirstDynamicColumn) {
            return <RowLoadError />;
          }
          return <span className="text-muted">—</span>;
        }
        if (originalCustomComponent) {
          return originalCustomComponent(value, row);
        }
        if (originalColumnType === ColumnTypes.Custom) {
          return <span />;
        }
        return <span>{value !== null && value !== undefined ? value : ""}</span>;
      },
    };
  });
};

const AggregatedSummaryList = ({ config }) => {
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const { rows, loading, totalCount, refresh, showRefresh } = useAggregatedSummaryList(config);

  const tableConfig = config.table || {};
  const modalConfig = config.modal;
  const isManifestMode = config.mode === "manifest-driven";

  const handlers = useMemo(
    () => ({
      onPriorityClick: (row) => {
        setSelectedRow(row.original);
        modalRef.current?.handleShow();
      },
      navigate,
    }),
    [navigate]
  );

  const columns = useMemo(() => {
    const baseColumns = tableConfig.getColumns ? tableConfig.getColumns(handlers) : [];
    if (!isManifestMode) {
      return baseColumns;
    }
    return wrapColumnsWithRowLoading(baseColumns, {
      staticColumnAccessors: config.manifest?.staticColumns || [],
    });
  }, [config.manifest, handlers, isManifestMode, tableConfig]);

  const modalColumns = useMemo(
    () => (modalConfig?.getColumns ? modalConfig.getColumns(handlers) : []),
    [handlers, modalConfig]
  );

  const tableData = Array.isArray(rows) ? rows : [];

  const handleRowClick = (row) => {
    if (config.rowClick?.onRowClick) {
      config.rowClick.onRowClick(row, navigate);
    }
  };

  const handleModalRowClick = (row) => {
    if (modalConfig?.onModalRowClick && selectedRow) {
      modalConfig.onModalRowClick(selectedRow, row, navigate);
    }
  };

  const nestedRows =
    selectedRow && modalConfig?.getNestedRows ? modalConfig.getNestedRows(selectedRow) : [];
  const showTotal = typeof config.header?.totalCountColumn === "string";

  return (
    <div className="p-4">
      <div className="fax-program-list-panel">
        <div className="codex-version-display-bar fax-program-list-panel__header fax-program-list-panel__header--summary">
          {showTotal ? (
            <span className="list-header-meta">
              {config.header.totalLabel}: <strong>{totalCount}</strong>
            </span>
          ) : (
            <span className="list-header-meta" aria-hidden="true" />
          )}
          <div className="list-header-title-wrap">
            <strong className="list-header-title">{config.header?.title}</strong>
            {showRefresh && (
              <OverlayTrigger overlay={<Tooltip>Refresh Data</Tooltip>}>
                <MdRefresh
                  data-testid={config.header?.refreshTestId || "aggregated-summary-list-refresh"}
                  onClick={refresh}
                  className="fs-5 list-header-refresh-btn"
                  role="button"
                  style={{ cursor: "pointer" }}
                />
              </OverlayTrigger>
            )}
          </div>
        </div>
        <div className="fax-program-list-panel__body">
          <ClientSideReactTable
            columnDefination={columns}
            data={tableData}
            showPagination={tableConfig.showPagination ?? false}
            pageSize={tableConfig.pageSize ?? 25}
            onRowClick={handleRowClick}
            rowClassName={tableConfig.rowClassName || ""}
            loading={isManifestMode ? false : loading}
            overrideRowId={isManifestMode ? "rowId" : tableConfig.overrideRowId}
          />
        </div>
      </div>
      {modalConfig?.enabled && (
        <DismissableModal
          ref={modalRef}
          title={modalConfig.title}
          openModalButton={<></>}
          size={modalConfig.size || "lg"}
          centered
          draggable={false}
        >
          {nestedRows.length > 0 ? (
            <div className="overflow-auto" style={{ maxHeight: 400 }}>
              <ClientSideReactTable
                columnDefination={modalColumns}
                data={nestedRows}
                showPagination={false}
                onRowClick={handleModalRowClick}
                rowClassName="clickable-row"
              />
            </div>
          ) : (
            <p>{modalConfig.emptyMessage || "No details available."}</p>
          )}
        </DismissableModal>
      )}
    </div>
  );
};

export default AggregatedSummaryList;
